import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { OrbitalRings } from "../ui/OrbitalRings";
import { ImageWithFallback } from "../ImageWithFallback";
import avatar from "../../assets/962ef5add980d65c7efa5147f6451803674674bf.png";
import { BRAND, BOOKING_URL, SOCIAL_LINKS } from "../../lib/brand";
import { HEADLINE, AVAILABILITY, CREDENTIALS } from "../../data/profileData";
import { CvFinder } from "./CvFinder";

export function ProfileHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const ringRotations = [
    useTransform(scrollYProgress, [0, 1], [0, 300]),
    useTransform(scrollYProgress, [0, 1], [0, -220]),
    useTransform(scrollYProgress, [0, 1], [0, 150]),
    useTransform(scrollYProgress, [0, 1], [0, -80]),
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMouse({ x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center overflow-hidden px-4 md:px-[116px] pt-[140px] pb-16 md:pb-24"
      style={{ perspective: "900px" }}
    >
      <OrbitalRings mouse={mouse} scrollRotations={ringRotations} variant="flat" className="opacity-50" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto grid gap-10 lg:grid-cols-[minmax(0,1fr)_440px] lg:items-start">
        {/* ── Left: the decision content ── */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4 lg:hidden">
            <ImageWithFallback src={avatar} alt="Olga Molina" className="w-[76px] h-[76px] rounded-full" />
            <p className="font-mono text-[12px] tracking-[2px] uppercase text-[#003e5b]">{HEADLINE.name}</p>
          </div>
          <p className="hidden lg:block font-mono text-[13px] tracking-[3px] uppercase text-[#003e5b]">
            {HEADLINE.name}
          </p>

          <h1 className="font-black text-[38px] md:text-[64px] leading-[1.05] tracking-tight text-[#141414] max-w-[820px]">
            {HEADLINE.title},{" "}
            <span style={{ color: BRAND.blue }}>{HEADLINE.specialism}</span>.
          </h1>

          <p className="text-[17px] md:text-[21px] leading-[1.55] text-[#666] max-w-[640px]">{HEADLINE.summary}</p>

          {/* Primary actions */}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full border-2 border-[#141414] px-7 py-4 font-bold text-[13px] tracking-[2px] uppercase text-[#003e5b] shadow-[0_6px_0_-1px_rgba(0,0,0,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_9px_0_-1px_rgba(0,0,0,0.22)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#003e5b]/30"
              style={{ background: BRAND.greenBright }}
            >
              Book a 20-min call
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
                →
              </span>
            </a>

            <CvFinder />

            <a
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-4 font-bold text-[13px] tracking-[2px] uppercase text-[#003e5b] underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b] rounded-full"
            >
              LinkedIn
            </a>
          </div>

          {/* Credential proof, immediately under the ask */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
            {CREDENTIALS.map((cert) => (
              <a
                key={cert.name}
                href={cert.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-baseline gap-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b] rounded"
              >
                <span aria-hidden="true" style={{ color: BRAND.green }}>
                  ✓
                </span>
                <span className="font-semibold text-[#141414] group-hover:underline underline-offset-4">
                  {cert.name}
                </span>
                <span className="text-[#666]">{cert.detail}</span>
              </a>
            ))}
          </div>
        </div>

        {/* ── Right: the facts card ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
        >
        <motion.aside
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
          className="rounded-[32px] border-2 border-[#141414] bg-white p-7 md:p-9 shadow-[0px_18px_40px_-14px_rgba(0,62,91,0.4)]"
          aria-labelledby="engagement-facts"
        >
          <div className="hidden lg:flex items-center gap-4 mb-6">
            <ImageWithFallback src={avatar} alt="Olga Molina" className="w-[76px] h-[76px] rounded-full" />
            <div className="flex flex-col">
              <span className="font-black text-[20px] leading-tight text-[#141414]">{HEADLINE.name}</span>
              <span className="text-[14px] text-[#666]">{AVAILABILITY.location}</span>
            </div>
          </div>

          <h2 id="engagement-facts" className="font-mono text-[11px] tracking-[2px] uppercase text-[#666] mb-4">
            The practical bits
          </h2>

          <dl className="flex flex-col divide-y divide-[#141414]/10">
            {[
              { term: "Rate", value: AVAILABILITY.rate },
              { term: "Working", value: AVAILABILITY.remote },
              { term: "Contracting", value: AVAILABILITY.entity },
            ].map((row) => (
              <div key={row.term} className="flex justify-between gap-4 py-3.5">
                <dt className="text-[14px] text-[#666] shrink-0">{row.term}</dt>
                <dd className="text-[15px] font-medium text-[#141414] text-right">{row.value}</dd>
              </div>
            ))}
          </dl>
        </motion.aside>
        </motion.div>
      </div>
    </section>
  );
}
